import React, { useEffect, useState, useMemo } from "react";
import { Box } from "@mui/material";
import Gantt from "../../../component/gantt/index.js";
// import TreeTable from "../../../component/table/TreeTable.js";
import TreeTable from "../../../component/table/VirtualTreeTable.js";
import dayjs from "dayjs";

const spanStyle = {
  width: "36px",
  height: "12px",
  borderRadius: "4px",
  mr: "6px",
};

const ScheduleGantt = React.memo(({
  tasks,
  currentDate,
  setCurrentDate,
  startDate,
  endDate,
  handleRowClick,
  onRowEdit,
  ...props
}) => {
  const [state, setState] = useState({
    expandedTasks: [],
    radioValue: "Month",
    showPopup: false,
    taskTableLayout: props.taskTableLayout || false,
  });
  const treeTableHeaders = useMemo(() => props.treeTableHeaders || [
    { label: "Task ID", width: "100px" },
    { label: "Task Name", width: "170px" },
    { label: "", width: "20px" },
    { label: "Plan Start", width: "85px" },
    { label: "Plan End", width: "85px" },
    { label: "Actual Start", width: "85px" },
    { label: "Actual End", width: "85px" },
    { label: "Contractor", width: "100px" },
    { label: "Work Type", width: "100px" },
  ], [props.treeTableHeaders]);
  const editableColumns = useMemo(() => props.editableColumns || [], [props.editableColumns]);
  const [filteredGanttData, setFilteredGanttData] = useState(null);
  const handleToggleOpen = (id) => {
    setState((prevState) => {
      const { expandedTasks } = prevState;
      if (expandedTasks.includes(id)) {
        return {
          ...prevState,
          expandedTasks: expandedTasks.filter((taskId) => taskId !== id),
        };
      } else {
        return {
          ...prevState,
          expandedTasks: [...expandedTasks, id],
        };
      }
    });
  };

  const handleExpandAll = (ids) => {
    setState((prevState) => ({
      ...prevState,
      expandedTasks: ids,
    }));
  };

  const handleCollapseAll = (id) => {
    setState((prevState) => ({
      ...prevState,
      expandedTasks: id,
    }));
  };

  const handleRadioChange = (val) => {
    setState((prevState) => ({
      ...prevState,
      radioValue: val,
    }));
  };

  const filterGanttData = (data) => {
    return data.filter((task) => {
      if (!task.parentId) return true;
      return state.expandedTasks.includes(task.parentId);
    });
  };

  const formatDateString = (dateString) => {
    if (!dateString) return "";
    return dayjs(dateString).format("YYYY-MM-DD");
  };

  const transformData = (treeData) => {
    const result = [];
    const processTask = (task, parentId = null) => {
      // Plan data
      result.push({
        start: formatDateString(task.planStart),
        end: formatDateString(task.planEnd),
        id: `${task.taskId}_plan`,
        name: task.taskId,
        parentId: parentId,
        dependencies: task.predecessor
          ? task.predecessor.split(",").map((p) => `${p}_plan`)
          : null,
        custom_class: "plan-bar",
      });
      result.push({
        start: formatDateString(task.actualStart),
        end: formatDateString(task.actualEnd),
        id: `${task.taskId}_actual`,
        name: task.taskId,
        parentId: parentId,
        // dependencies: task.predecessor ? task.predecessor.split(",").map((p) => `${p}_actual`) : null,
        custom_class: "actual-bar",
      });

      if (task.children && task.children.length > 0) {
        task.children.forEach((child) => {
          processTask(child, task.taskId);
        });
      }
    };

    treeData.forEach((task) => {
      processTask(task);
    });

    return result;
  };

  useEffect(() => {
    setState((prevState) => ({
      ...prevState,
      taskTableLayout: props.taskTableLayout || false,
    }));
  }, [props.taskTableLayout]);

  useEffect(() => {
    setFilteredGanttData(filterGanttData(transformData(tasks)));
  }, [tasks, state.expandedTasks]);

  const [scrollPosition, setScrollPosition] = useState(0);

  const handleTableScroll = (scrollTop) => {
    setScrollPosition(scrollTop);
    const container = document.querySelector(".gantt-container");
    if (container && scrollTop !== container.scrollTop) {
      container.scrollTop = scrollTop;
    }
  };

  const handleGanttScroll = (scrollTop) => {
    setScrollPosition(scrollTop);
  };

  // useEffect(() => {
  //   if (!filteredGanttData) return;
  //   new Gantt(
  //     ".gantt-target",
  //     filteredGanttData,
  //     optionsGantt,
  //     (dateId) => {
  //       setCurrentDate(dateId);
  //     },
  //     handleGanttScroll
  //   );
  // }, [filteredGanttData, optionsGantt]);

  // useEffect(() => {
  //   setOptionsGantt((prevOptions) => ({
  //     ...prevOptions,
  //     view_mode: state.radioValue,
  //     lineStart: startDate,
  //     lineEnd: endDate,
  //     lineCurrent: currentDate,
  //     selectedRowId: props.selectedRowId,
  //     miniColumnWidth: props.miniColumnWidth,
  //   }));
  // }, [state.radioValue, startDate, endDate, currentDate, props.selectedRowId]);

  useEffect(() => {
    if (!filteredGanttData) return;
    const ganttOptions = {
      view_mode: state.radioValue,
      lineStart: startDate,
      lineEnd: endDate,
      lineCurrent: currentDate,
      selectedRowId: props.selectedRowId,
      miniColumnWidth: props.miniColumnWidth,
      table_row_height: props.tableRowHeight || 24.72,
      on_click(task) { },
    };

    new Gantt(
      ".gantt-target",
      filteredGanttData,
      ganttOptions,
      (dateId) => setCurrentDate(dateId),
      handleGanttScroll
    );
  }, [
    filteredGanttData,
    state.radioValue,
    startDate,
    endDate,
    currentDate,
    props.selectedRowId,
    props.miniColumnWidth,
    props.tableRowHeight
  ]);

  return (
    <Box
      height="100%"
      width="100%"
      display="flex"
      sx={{
        border: "1px solid #D0D5DE",
        borderRadius: "10px",
        overflow: "hidden",
      }}
    >
      <Box
        className="left-table"
        sx={{
          borderRight: "1px solid #ccc",
          width: state.taskTableLayout ? "100%" : "calc(50% - 8px)",
          minWidth: props.showButton ? 640 : 360,
          height: "100%",
          mr: 1,
          position: "relative",
        }}
      >
        <TreeTable
          treeTableHeaders={treeTableHeaders}
          editableColumns={editableColumns}
          onRowEdit={onRowEdit}
          rows={tasks}
          ganttscrollTop={scrollPosition}
          onRowClick={handleRowClick}
          onToggleOpen={handleToggleOpen}
          onRadioClick={handleRadioChange}
          onExpandAll={handleExpandAll}
          onCollapseAll={handleCollapseAll}
          onScroll={handleTableScroll}
          onImageClick={() => {
            setState((prevState) => ({
              ...prevState,
              taskTableLayout: !prevState.taskTableLayout,
            }));
          }}
          showButton={props.showButton}
          SimplifiedVersion={props.SimplifiedVersion}
          buttonOnClick={props.buttonOnClick}
          buttonIcon={props.buttonIcon}
          isLoading={props.isLoading}
          defaultExpandAll={props.defaultExpandAll}
          estimateSize={props.estimateSize}
        />
      </Box>
      <Box
        className="right-gantt"
        sx={{
          borderLeft: "1px solid #ccc",
          width: "50%",
          maxWidth: `calc(100% - ${props.showButton ? 640 : 360}px - 8px)`,
          display: state.taskTableLayout ? "none" : "block",
          borderRadius: "0 10px 0 0",
          overflow: "hidden",
        }}
      >
        <div
          className="gantt-target"
          style={{ height: "calc(100% - 50px)" }}
        ></div>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="flex-end"
          sx={{ p: 2 }}
        >
          <Box sx={{ ...spanStyle, backgroundColor: "#A9E2CD" }}></Box>
          <span>Plan progress</span>
          <Box
            sx={{ ...spanStyle, backgroundColor: "#318E6C", ml: "24px" }}
          ></Box>
          <span>Actual progress</span>
        </Box>
      </Box>
    </Box>
  );
});

export default ScheduleGantt;
